import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import {
  CheckOutlined,
  DeleteOutlined,
  DownOutlined,
  EditOutlined,
  LeftOutlined,
} from '@ant-design/icons';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button,
  ConfigProvider,
  Divider,
  Dropdown,
  Input,
  Popconfirm,
  Table,
  TableColumnsType,
  Typography,
} from 'antd';
import { MenuProps } from 'antd/lib';
import uniqBy from 'lodash/uniqBy';
import * as yup from 'yup';

import { useAppDispatch, useAppSelector } from '@/core/redux/hooks';
import { airExchangeTableActions } from '@/core/redux/slices/airExchangeTable/slice';
import { systemModalsActions } from '@/core/redux/slices/modals/smokeExtraction/system/slice';
import { reportActions } from '@/core/redux/slices/smokeExtraction/system/report/slice';
import { systemSelectors } from '@/core/redux/slices/smokeExtraction/system/selectors';
import { systemActions } from '@/core/redux/slices/smokeExtraction/system/slice';

const { Title, Text } = Typography;

export const CurrentTablePage: React.FC = () => {
  const [isEditView, setIsEditView] = useState<boolean>(false);
  const [editValue, setEditValue] = useState<string>('');
  const [filterState, setFilterState] = useState<any>({
    floor: [],
    block: [],
    name: [],
    system: [],
  });
  const [filteredData, setFilteredData] = useState<any>(null);

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const routerParams = useParams();

  const systemID = Number(routerParams.id);

  const system = useAppSelector(systemSelectors.system);
  const can_be_edited = system?.is_editable;

  useEffect(() => {
    dispatch(
      systemActions.fetchSystem({
        id: systemID,
      })
    );
    return () => {
      dispatch(systemActions.setSystem(null));
    };
  }, []);

  useEffect(() => {
    if (system) {
      let data = system?.state ? [...system?.state?.table] : [];

      if (filterState?.floor?.length) {
        data = data?.filter((el: any) =>
          filterState?.floor?.includes(el?.floor)
        );
      }

      if (filterState?.block?.length) {
        data = data?.filter((el: any) =>
          filterState?.block?.includes(el?.block)
        );
      }

      if (filterState?.name?.length) {
        data = data?.filter((el: any) => filterState?.name?.includes(el?.name));
      }

      if (filterState?.system?.length) {
        data = data?.filter(
          (el: any) =>
            filterState?.system?.includes(el?.sys_inflow) ||
            filterState?.system?.includes(el?.sys_exhaust) ||
            filterState?.system?.includes(el?.sys_mo_inflow) ||
            filterState?.system?.includes(el?.sys_mo_exhaust)
        );
      }

      setFilteredData(data);
    }
  }, [filterState, system]);

  const schema = yup.object().shape({
    name_system: yup.string().required('Это поле обязательно для заполнения'),
  });

  const methods = useForm({
    resolver: yupResolver(schema),
  });

  const getOptions = (typeOption: string) => {
    const data: any = uniqBy(
      system?.state?.table?.map((el: any) => {
        const value: string = el?.[typeOption];
        return { label: value, key: value };
      }),
      'key'
    ).sort((a: any, b: any) => a?.key - b?.key);
    const options: MenuProps['items'] = data;
    return options;
  };

  const getSystemOptions: any = () => {
    const data: any = [];
    system?.state?.table?.forEach((el: any) => {
      if (el?.sys_inflow)
        data?.push({ label: el?.sys_inflow, key: el?.sys_inflow });
      if (el?.sys_exhaust)
        data?.push({ label: el?.sys_exhaust, key: el?.sys_exhaust });
      if (el?.sys_mo_inflow)
        data?.push({ label: el?.sys_mo_inflow, key: el?.sys_mo_inflow });
      if (el?.sys_mo_exhaust)
        data?.push({ label: el?.sys_mo_exhaust, key: el?.sys_mo_exhaust });
    });

    return uniqBy(data, 'key');
  };

  const downloadReport = () => {
    dispatch(
      reportActions.createReport({
        id: systemID,
        extension: 'xlsx',
      })
    );
  };

  const deleteRow = (id: string) => {
    const remove_item = system?.state?.table?.find((el: any) => el?.id === id);

    const filteredTable = system?.state?.table?.filter(
      (el: any) => el?.id !== id
    );
    const result = filteredTable?.map((el: any) => {
      const obj = { ...el };
      if (obj?.id === remove_item?.exhaust_linked) {
        obj.exhaust_linked = null;

        obj.flow_inflow =
          obj.flow_inflow -
          (Number(remove_item?.flow_exhaust) -
            Number(remove_item?.flow_inflow));
      }
      if (obj?.id === remove_item?.inflow_linked) {
        obj.inflow_linked = null;

        obj.flow_exhaust =
          obj.flow_exhaust -
          (Number(remove_item?.flow_inflow) -
            Number(remove_item?.flow_exhaust));
      }
      if (obj?.inflow_linked === remove_item?.id) {
        obj.inflow_linked = null;
      }

      if (obj?.exhaust_linked === remove_item?.id) {
        obj.exhaust_linked = null;
      }
      return obj;
    });

    system?.id &&
      dispatch(
        airExchangeTableActions.performAirExchangeTable({
          calc_id: system?.id,
          data: { table: result },
        })
      );
  };

  const columns: TableColumnsType<any> = [
    {
      title: 'Корпус/Отсек или иное',
      dataIndex: 'block',
      align: 'center',
      width: 110,
    },

    {
      title: 'Этаж/Уровень',
      dataIndex: 'floor',
      align: 'center',
      width: 70,
    },
    {
      title: '№ помещения',
      dataIndex: 'num',
      align: 'center',
      width: 100,
    },
    {
      title: <Text className='text-nowrap'>Наименование помещения</Text>,
      dataIndex: 'name',
      align: 'start',
      // className: 'min-w-[350px]',
      width: 220,
    },
    {
      title: (
        <Text className='font-bold text-nowrap'>
          Площадь, <br />
          м²
        </Text>
      ),
      dataIndex: 'area',
      align: 'center',
      width: 80,
    },

    {
      title: (
        <Text className='font-bold text-nowrap'>
          Высота, <br /> м
        </Text>
      ),
      dataIndex: 'height',
      align: 'center',
      width: 70,
    },
    {
      title: (
        <Text className='font-bold text-nowrap'>
          Объем, <br /> м³
        </Text>
      ),
      dataIndex: 'volume',
      align: 'center',
      width: 65,
    },

    {
      title: 'Кол-во людей',
      dataIndex: 'people_count',
      align: 'center',
      width: 65,
    },
    {
      title: 'Кратность об/ч',
      children: [
        {
          title: 'Приток',
          dataIndex: 'multiplicity_inflow',
          align: 'center',
          width: 80,
          render: (text, record) =>
            record?.type_inflow !== 'По кратности' &&
            record?.multiplicity_inflow ? (
              <div>{record?.multiplicity_inflow} (расч.)</div>
            ) : (
              <>{record?.multiplicity_inflow}</>
            ),
        },
        {
          title: 'Вытяжка',
          dataIndex: 'multiplicity_exhaust',
          align: 'center',
          width: 80,
          render: (text, record) =>
            record?.type_exhaust !== 'По кратности' &&
            record?.multiplicity_exhaust ? (
              <div>{record?.multiplicity_exhaust} (расч.)</div>
            ) : (
              <>{record?.multiplicity_exhaust}</>
            ),
        },
      ],
    },
    {
      title: 'Расход воздуха м³/ч',
      children: [
        {
          title: 'Приток',
          dataIndex: 'flow_inflow',
          align: 'center',
          width: 80,
        },
        {
          title: 'Вытяжка',
          dataIndex: 'flow_exhaust',
          align: 'center',
          width: 80,
        },
      ],
    },

    {
      title: <Text className='text-nowrap'>Расход воздуха М.О. м³/ч</Text>,
      children: [
        {
          title: 'Приток',
          dataIndex: 'flow_mo_inflow',
          align: 'center',
          width: 95,
        },
        {
          title: 'Вытяжка',
          dataIndex: 'flow_mo_exhaust',
          align: 'center',
          width: 95,
        },
      ],
    },

    {
      title: 'Обозначение системы',
      className: 'text-nowrap',
      children: [
        {
          title: <Text className='text-nowrap'>Приток </Text>,
          dataIndex: 'sys_inflow',
          width: 80,
          align: 'center',
        },
        {
          title: <Text className='text-nowrap'>Вытяжка</Text>,
          dataIndex: 'sys_exhaust',
          width: 80,
          align: 'center',
        },
        {
          title: <Text className='text-nowrap'>Приток М.О.</Text>,
          dataIndex: 'sys_mo_inflow',
          width: 110,
          align: 'center',
        },
        {
          title: <Text className='text-nowrap'>Вытяжка М.О.</Text>,
          dataIndex: 'sys_mo_exhaust',
          width: 110,
          align: 'center',
        },
      ],
    },
    {
      title: 'Примечание',
      dataIndex: 'note',
      align: 'start',
      className: 'min-w-[200px]',
      width: 250,
    },
    {
      title: '',
      dataIndex: '',
      fixed: 'right',
      width: 83,
      render: (text, record) => (
        <div className='flex gap-2'>
          <Button
            disabled={record?.is_mo || !can_be_edited}
            onClick={(e) => {
              dispatch(
                airExchangeTableActions.setAddRoomModal({
                  isRender: true,
                  data: record,
                })
              );
            }}
            icon={<EditOutlined />}
          />

          <Popconfirm
            title='Вы уверены, что хотите удалить эту строку?'
            okText='Удалить'
            cancelText='Отменить'
            placement='left'
            onConfirm={() => deleteRow(record?.id)}
          >
            <Button
              disabled={record?.is_mo || !can_be_edited}
              icon={<DeleteOutlined />}
            />
          </Popconfirm>
        </div>
      ),
    },
  ];

  const openSystemModal = () => {
    dispatch(
      airExchangeTableActions.setAirExchangeTableSystemModal({
        isRender: true,
      })
    );
  };

  const openAddRoomModal = () => {
    dispatch(
      airExchangeTableActions.setAddRoomModal({
        isRender: true,
      })
    );
  };

  const openExplicationModal = () => {
    dispatch(
      airExchangeTableActions.setExplicationModal({
        isRender: true,
        systemID: systemID,
      })
    );
  };

  const saveNewNameSystem = () => {
    setIsEditView(false);
    dispatch(
      airExchangeTableActions.updateNameSystem({
        systemID: systemID,
        name: editValue,
      })
    );
  };

  const handleDropdown = (event: any, key: string) => {
    if (filterState?.[key]?.includes(event?.key)) {
      const result = filterState?.[key]?.filter(
        (el: string) => el !== event?.key
      );
      setFilterState((prev: any) => {
        return { ...prev, [key]: result };
      });
    } else {
      setFilterState((prev: any) => {
        return { ...prev, [key]: [...filterState?.[key], event?.key] };
      });
    }
  };

  const checkFilters = () => {
    return (
      filterState?.floor?.length > 0 ||
      filterState?.block?.length > 0 ||
      filterState?.name?.length > 0 ||
      filterState?.system?.length > 0
    );
  };

  const clearFilters = () => {
    setFilterState({
      floor: [],
      block: [],
      name: [],
      system: [],
    });
  };

  const getSummary = (data: any) => {
    let sum_inflow = 0;
    let sum_exhaust = 0;
    let sum_flow_mo_inflow = 0;
    let sum_flow_mo_exhaust = 0;
    data?.length &&
      data.forEach((el: any) => {
        if (el?.flow_inflow) {
          sum_inflow += el?.flow_inflow;
        }

        if (el?.flow_exhaust) {
          sum_exhaust += el?.flow_exhaust;
        }

        if (el?.flow_mo_inflow) {
          sum_flow_mo_inflow += el?.flow_mo_inflow;
        }

        if (el?.flow_mo_exhaust) {
          sum_flow_mo_exhaust += el?.flow_mo_exhaust;
        }
      });

    return (
      <Table.Summary fixed={'bottom'}>
        {(sum_inflow !== 0 ||
          sum_exhaust !== 0 ||
          sum_flow_mo_inflow !== 0 ||
          sum_flow_mo_exhaust !== 0) && (
          <Table.Summary.Row>
            <Table.Summary.Cell
              index={0}
              colSpan={10}
              align='end'
              className='font-bold'
            >
              Итого:
            </Table.Summary.Cell>
            <Table.Summary.Cell index={1} align='center' className='font-bold'>
              {sum_inflow || ''}
            </Table.Summary.Cell>
            <Table.Summary.Cell index={2} align='center' className='font-bold'>
              {sum_exhaust || ''}
            </Table.Summary.Cell>
            <Table.Summary.Cell index={3} align='center' className='font-bold'>
              {sum_flow_mo_inflow || ''}
            </Table.Summary.Cell>
            <Table.Summary.Cell index={4} align='center' className='font-bold'>
              {sum_flow_mo_exhaust || ''}
            </Table.Summary.Cell>
            <Table.Summary.Cell index={5} colSpan={6}></Table.Summary.Cell>
          </Table.Summary.Row>
        )}
        <Table.Summary.Row>
          <Table.Summary.Cell index={6} colSpan={20}>
            <div className='flex gap-2 pt-1 pb-1'>
              <Button
                disabled={!can_be_edited}
                type='primary'
                onClick={openAddRoomModal}
              >
                Добавить помещение
              </Button>
              <Button
                disabled={system?.state?.table?.length || !can_be_edited}
                onClick={openExplicationModal}
              >
                Загрузить экспликацию
              </Button>
              <Button disabled={!can_be_edited} onClick={openSystemModal}>
                Расход воздуха по системам
              </Button>
            </div>
          </Table.Summary.Cell>
        </Table.Summary.Row>
      </Table.Summary>
    );
  };

  const plugin_options: MenuProps['items'] = [
    {
      key: 1,
      label: 'Воздухообмен: Экспорт в плагин',
    },
    {
      key: 2,
      label: 'Воздухообмен: Импорт из плагина',
    },
    {
      key: 3,
      label: 'Руководство по работе с плагином',
    },
  ];

  const handle_plugin_button = (e: any) => {
    if (e?.key === '1') {
      dispatch(
        airExchangeTableActions.exportJSON({
          id: systemID,
          name: system?.name || '',
        })
      );
    } else if (e?.key === '2') {
      dispatch(
        systemModalsActions.openImportFromBIMModal({
          typeAction: 'airExchangeTable',
          systemID: systemID,
        })
      );
    } else if (e?.key === '3') {
      window.open('https://aeropro.expert/rukovodstvo-plagin');
    }
  };

  return (
    <div className='h-full p-6 pb-0 overflow-auto'>
      <div className='bg-white h-fit p-6 flex'>
        <FormProvider {...methods}>
          <div className='h-full w-full'>
            <div className='flex justify-between items-center'>
              <div className='flex gap-4 items-center text-wrap'>
                <Button
                  icon={<LeftOutlined className='p-2' />}
                  onClick={() => navigate('/air-exchange-table')}
                />

                <div className='flex flex-col'>
                  <div className='flex gap-2 items-center'>
                    {isEditView ? (
                      <>
                        <Input
                          defaultValue={system?.name}
                          onChange={(e) => setEditValue(e.currentTarget.value)}
                          value={editValue || system?.name}
                        />
                        <CheckOutlined
                          className='text-primary cursor-pointer'
                          onClick={saveNewNameSystem}
                        />
                      </>
                    ) : (
                      <>
                        <Title level={5} className='!m-0'>
                          {system?.name}
                        </Title>
                        <EditOutlined
                          className='text-primary cursor-pointer'
                          onClick={() => {
                            setIsEditView(true);
                          }}
                        />
                      </>
                    )}
                  </div>

                  <div className='flex gap-2 items-center flex-wrap'>
                    <Text>Таблица воздухообмена</Text>
                    <Divider type='vertical' />
                    <Text type='secondary'>Сохраняется автоматически...</Text>
                  </div>
                </div>
              </div>
              <div className='flex gap-1 flex-wrap justify-end '>
                <Dropdown
                  disabled={!can_be_edited}
                  menu={{
                    items: plugin_options,
                    onClick: handle_plugin_button,
                  }}
                  trigger={['click']}
                >
                  <Button>
                    BIM ↔ Аэропро.Эксперт <DownOutlined />
                  </Button>
                </Dropdown>

                <Button
                  onClick={() =>
                    window.open(
                      'https://aeropro.expert/rukovodstvo-po-prilojenie-14'
                    )
                  }
                >
                  Гид по модулю
                </Button>
                <Button
                  disabled={!system?.state?.finished || !can_be_edited}
                  onClick={downloadReport}
                  type='primary'
                >
                  Сформировать расчет
                </Button>
              </div>
            </div>
            <div className='flex mt-4 gap-2'>
              <Dropdown
                menu={{
                  items: getOptions('block'),
                  onClick: (e) => handleDropdown(e, 'block'),
                  selectable: true,
                  multiple: true,
                  selectedKeys: filterState?.block,
                }}
                trigger={['click']}
              >
                <Button>
                  Корпус <DownOutlined />
                </Button>
              </Dropdown>

              <Dropdown
                menu={{
                  items: getOptions('floor'),
                  onClick: (e) => handleDropdown(e, 'floor'),
                  selectable: true,
                  multiple: true,
                  selectedKeys: filterState?.floor,
                }}
                trigger={['click']}
              >
                <Button>
                  Этаж <DownOutlined />
                </Button>
              </Dropdown>

              <Dropdown
                menu={{
                  items: getOptions('name'),
                  onClick: (e) => handleDropdown(e, 'name'),
                  selectable: true,
                  multiple: true,
                  selectedKeys: filterState?.name,
                }}
                trigger={['click']}
              >
                <Button>
                  Помещение <DownOutlined />
                </Button>
              </Dropdown>

              <Dropdown
                menu={{
                  items: getSystemOptions(),
                  onClick: (e) => handleDropdown(e, 'system'),
                  selectable: true,
                  multiple: true,
                  selectedKeys: filterState?.system,
                }}
                trigger={['click']}
              >
                <Button>
                  Обозначение системы <DownOutlined />
                </Button>
              </Dropdown>
              {checkFilters() && (
                <Button type='link' className='p-0' onClick={clearFilters}>
                  сбросить фильтры
                </Button>
              )}
            </div>

            <ConfigProvider
              theme={{
                components: {
                  Table: {
                    cellPaddingBlock: 5,
                    cellPaddingInline: 5,
                  },
                },
              }}
            >
              <Table
                bordered
                rowClassName='text-center'
                rowKey={(record) => record?.id}
                dataSource={filteredData}
                columns={columns}
                className='mt-4'
                scroll={{
                  x: 'max-content',
                }}
                pagination={false}
                sticky={{ offsetHeader: -24 }}
                summary={getSummary}
                onRow={(record) => ({
                  onDoubleClick: () =>
                    !record?.is_mo &&
                    can_be_edited &&
                    dispatch(
                      airExchangeTableActions.setAddRoomModal({
                        isRender: true,
                        data: record,
                      })
                    ),
                })}
              />
            </ConfigProvider>
          </div>
        </FormProvider>
      </div>
    </div>
  );
};
